import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../layouts/AdminLayout';
import { getFeedbacks, updateFeedback } from '../../services/feedbackService';
import FeedbackDetailDialog from './FeedbackDetailDialog';
import Pagination from '../../components/Pagination';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Box, Typography, CircularProgress, Snackbar, IconButton
} from '@material-ui/core';
import {  makeStyles , useTheme } from "@material-ui/core/styles";
import VisibilityIcon from '@material-ui/icons/Visibility';
import ReplyIcon from '@material-ui/icons/Reply';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}` },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400 },
  actionBtn: {
    color: theme.palette.text.secondary,
    padding: 8,
    '&:hover': { backgroundColor: theme.palette.action.hover, color: theme.palette.text.primary } },
  countChip: {
    background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.15) 100%)',
    color: '#10b981',
    border: '1px solid rgba(16,185,129,0.3)',
    fontWeight: 700,
    fontSize: '0.85rem',
    height: 36,
    padding: '0 8px',
    borderRadius: 10,
  } }));

const categoryColors = {
  suggestion: '#10b981',
  bug_report: '#ef4444',
  usability_issue: '#f59e0b' };


export default function FeedbackListPage() {
  const classes = useStyles();
  
  const theme = useTheme();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const loadData = useCallback(async (opts = {}) => {
    setLoading(true);
    const targetPage = opts.page !== undefined ? opts.page : 1;
    try {
      const result = await getFeedbacks({ page: targetPage });
      setFeedbacks(result.items);
      setTotalCount(result.total);
      setTotalPages(result.totalPages);
      setPage(result.page);
    } catch (e) {
      console.error('Failed to load feedbacks:', e);
      setSnackbar({ open: true, message: 'Failed to load feedbacks' });
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData({ page: 1 }); }, [loadData]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    loadData({ page: newPage });
  };

  const handleView = (feedback) => {
    setSelectedFeedback(feedback);
    setDetailOpen(true);
  };

  const handleSaveReply = async (id, data, feedbackObj) => {
    try {
      await updateFeedback(id, {
        ...data,
        repliedAt: new Date(),
        acknowledged: true
      }, feedbackObj);
      setSnackbar({ open: true, message: 'Reply saved & notification sent to user!' });
      setDetailOpen(false);
      loadData({ page });
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to save reply' });
    }
  };


  const formatDate = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString();
  };

  return (
    <AdminLayout title="User Feedback">
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="body2" color="textSecondary">
          Review and respond to feedback submitted by app users.
        </Typography>
        <Chip label={`Total Feedback: ${totalCount}`} className={classes.countChip} />
      </Box>

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} />
          <Typography color="textSecondary" style={{ marginTop: 16 }}>Loading feedbacks...</Typography>
        </div>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Feedback</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {feedbacks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography style={{ color: theme.palette.text.secondary, padding: 24 }}>No feedback yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                feedbacks.map((fb) => (
                  <TableRow key={fb.id} hover>
                    <TableCell>
                      <Typography color="textPrimary" style={{ fontSize: '0.9rem' }}>
                        {fb.userName || 'Anonymous'}
                      </Typography>
                      <Typography style={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
                        {fb.userEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={fb.category}
                        size="small"
                        style={{
                          backgroundColor: `${categoryColors[fb.category] || '#64748b'}20`,
                          color: categoryColors[fb.category] || '#94a3b8',
                          fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography color="textSecondary" style={{ fontSize: '0.85rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {fb.feedback}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={fb.adminReply ? 'Replied' : fb.acknowledged ? 'Acknowledged' : 'New'}
                        size="small"
                        style={{
                          backgroundColor: fb.adminReply ? 'rgba(16,185,129,0.15)' : fb.acknowledged ? 'rgba(59,130,246,0.15)' : 'rgba(239,68,68,0.15)',
                          color: fb.adminReply ? '#10b981' : fb.acknowledged ? '#3b82f6' : '#ef4444',
                          fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography color="textSecondary" style={{ fontSize: '0.85rem' }}>
                        {formatDate(fb.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton className={classes.actionBtn} onClick={() => handleView(fb)}>
                        {fb.adminReply ? <ReplyIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && totalCount > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={20}
          onPageChange={handlePageChange}
        />
      )}

      {selectedFeedback && (
        <FeedbackDetailDialog
          open={detailOpen}
          feedback={selectedFeedback}
          onClose={() => setDetailOpen(false)}
          onSave={handleSaveReply}
        />
      )}

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </AdminLayout>
  );
}
